import { Submittal, SubmittalOrderedMap } from 'records/Submittal';
import { SubmittalQuery } from 'records/SubmittalQuery';

function filterStatus(
  submittal: Submittal,
  submittalQuery: SubmittalQuery
): boolean {
  if (submittalQuery.statuses.size === 0) {
    return true;
  }

  return submittalQuery.statuses.contains(submittal.status);
}

function filterCategory(
  submittal: Submittal,
  submittalQuery: SubmittalQuery
): boolean {
  if (submittalQuery.categories.size === 0) {
    return true;
  }

  return submittalQuery.categories.contains(submittal.category);
}

// function filterDueFromSub(
//   submittal: Submittal,
//   submittalQuery: SubmittalQuery
// ): boolean {
//   if (submittalQuery.dueFromSub > 0) {
//     return submittal.duefromsub < submittalQuery.dueFromSub;
//   }
//   return false;
// }

// function filterDueFromReviewer(
//   submittal: Submittal,
//   submittalQuery: SubmittalQuery
// ): boolean {
//   // if (submittalQuery.dueFromSub« > 0) {
//   //     return submittal.duefromsub < submittalQuery.dueFromSub ;
//   // }
//   return true;
// }

function filterSubmittals(
  submittals: SubmittalOrderedMap,
  submittalQuery: SubmittalQuery
): SubmittalOrderedMap {
  return submittals
    .filter((sub) => filterStatus(sub, submittalQuery))
    .filter((sub) => filterCategory(sub, submittalQuery));
}

interface SortHandlers {
  [key: string]: (
    submittals: SubmittalOrderedMap,
    direction: string
  ) => SubmittalOrderedMap;
}

const sortHandlers: SortHandlers = {
  submittalNumber: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a =
        direction === 'ascending'
          ? subA.specsectionnumber
          : subB.specsectionnumber;
      const b =
        direction === 'ascending'
          ? subB.specsectionnumber
          : subA.specsectionnumber;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      const a2 =
        direction === 'ascending' ? subA.sectioncount : subB.sectioncount;
      const b2 =
        direction === 'ascending' ? subB.sectioncount : subA.sectioncount;
      return a2 - b2;
    });

    return sortedSubmittals;
  },

  sectionCount: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a =
        direction === 'descending' ? subB.sectioncount : subA.sectioncount;
      const b =
        direction === 'descending' ? subA.sectioncount : subB.sectioncount;
      return a - b;
    });

    return sortedSubmittals;
  },

  submittalStatus: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a = direction === 'ascending' ? subA.status : subB.status;
      const b = direction === 'ascending' ? subB.status : subA.status;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSubmittals;
  },

  specSection: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a =
        direction === 'ascending' ? subA.specsectionname : subB.specsectionname;
      const b =
        direction === 'ascending' ? subB.specsectionname : subA.specsectionname;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSubmittals;
  },

  specDescription: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a =
        direction === 'ascending'
          ? subA.description.toLowerCase()
          : subB.description.toLowerCase();
      const b =
        direction === 'ascending'
          ? subB.description.toLowerCase()
          : subA.description.toLowerCase();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSubmittals;
  },

  specNumber: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a =
        direction === 'ascending'
          ? subA.specsectionnumber
          : subB.specsectionnumber;
      const b =
        direction === 'ascending'
          ? subB.specsectionnumber
          : subA.specsectionnumber;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSubmittals;
  },

  dueFromSub: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedPunches = submittals.sortBy((submittal): number => {
      return direction === 'ascending'
        ? submittal.duefromsub
        : -submittal.duefromsub;
    });

    return sortedPunches;
  },

  dueFromReviewer: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedPunches = submittals.sortBy((submittal): number => {
      return direction === 'ascending'
        ? submittal.duefromreviewer
        : -submittal.duefromreviewer;
    });

    return sortedPunches;
  },

  dueDate: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedPunches = submittals.sortBy((submittal): number => {
      const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
      if (subRequested || submittal.duefromreviewer < submittal.duefromsub) {
        return direction === 'ascending'
          ? submittal.duefromsub
          : -submittal.duefromsub;
      }
      return direction === 'ascending'
        ? submittal.duefromreviewer
        : -submittal.duefromreviewer;
    });

    return sortedPunches;
  },

  category: (
    submittals: SubmittalOrderedMap,
    direction
  ): SubmittalOrderedMap => {
    const sortedSubmittals = submittals.sort((subA, subB): number => {
      const a = direction === 'ascending' ? subA.category : subB.category;
      const b = direction === 'ascending' ? subB.category : subA.category;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSubmittals;
  },
};

function sortSubmittals(
  unsortedSubmittals: SubmittalOrderedMap,
  submittalQuery: SubmittalQuery
): SubmittalOrderedMap {
  const { sortField, sortDirection } = submittalQuery;

  if (sortHandlers[sortField]) {
    const sortedPunches = sortHandlers[sortField](
      unsortedSubmittals,
      sortDirection
    );

    return sortedPunches;
  }

  return unsortedSubmittals;
}

function searchSubmittals(
  submittals: SubmittalOrderedMap,
  submittalQuery: SubmittalQuery
): SubmittalOrderedMap {
  const { search } = submittalQuery;
  if (search.length === 0) {
    return submittals;
  }

  const sanitizedSearch = search.toLowerCase().replaceAll(/[.-\s]+/g, '');

  const results = submittals.filter((sub) => {
    // Number
    if (sub.number.toString().indexOf(search) === 0) {
      return true;
    }

    // Description
    const sanitizedDescription = sub.description
      .toLowerCase()
      .replaceAll(/[.-\s]+/g, '');

    if (sanitizedDescription.indexOf(sanitizedSearch) === 0) {
      return true;
    }

    // Spec Section Number
    const sanitizedSSNumber = sub.specsectionnumber
      .toLowerCase()
      .replaceAll(/[.-\s]+/g, '');

    // testing for both index 0 and index 1 because SSN can kinda have a leading 0.
    if (
      sanitizedSSNumber.indexOf(sanitizedSearch) === 0 ||
      sanitizedSSNumber.indexOf(sanitizedSearch) === 1
    ) {
      return true;
    }

    // Spec Section Name
    const sanitizedSSName = sub.specsectionname
      .toLowerCase()
      .replaceAll(/[.-\s]+/g, '');

    if (sanitizedSSName.indexOf(sanitizedSearch) === 0) {
      return true;
    }

    return false;
  });

  return results;
}

export default function querySubmittals(
  submittals: SubmittalOrderedMap,
  submittalQuery: SubmittalQuery
): SubmittalOrderedMap {
  const filteredSubmittals = filterSubmittals(submittals, submittalQuery);

  const searchedSubmittals = searchSubmittals(
    filteredSubmittals,
    submittalQuery
  );

  const sortedSubmittals = sortSubmittals(searchedSubmittals, submittalQuery);

  return sortedSubmittals;
}
